import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layers, Loader2, Download, RotateCcw } from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { updateDeviceField } from "@/utils/updateDeviceField";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";
import { sortOrganisationHierarchy } from "@/utils/organisationSort";
import { AI_MODEL_OPTIONS } from "@/components/devices/tabs/ai-workflows/aiModelSettings";
import { buildDeviceSteps, BulkChanges, BULK_CONCURRENCY, SkipError } from "@/lib/bulkDeviceUpdate";

type Status = "waiting" | "updating" | "done" | "failed" | "skipped";
interface Dev { device_id: string; device_name: string | null; organization_id: string | null; timezone: string | null; notification_ai_model: string | null }

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const INTERVALS = [[900000, "15 min"], [1800000, "30 min"], [3600000, "1 hour"], [7200000, "2 hours"], [10800000, "3 hours"], [14400000, "4 hours"], [18000000, "5 hours"], [21600000, "6 hours"]] as const;
const LEVELS = [1, 2, 3, 4, 5, 6];
const TIMEZONES = ["Europe/London", "Europe/Paris", "Africa/Johannesburg", "America/New_York", "America/Los_Angeles", "Australia/Sydney", "Asia/Dubai", "UTC"];
const WORKFLOWS = [["universal-uk-v1", "Universal"], ["highways-uk-v1", "Highways"], ["environmental-uk-v1", "Environmental"], ["custom_workflow", "Custom"]] as const;
const PRIVACY = [[0, "No Privacy"], [1, "Standard"], [2, "Maximum"]] as const;

/** Each row: tick to apply + its control. */
const Row = ({ on, setOn, label, children }: { on: boolean; setOn: (v: boolean) => void; label: string; children: React.ReactNode }) => (
  <div className="flex items-center gap-3 py-1.5">
    <Checkbox checked={on} onCheckedChange={v => setOn(v === true)} aria-label={`Apply ${label}`} />
    <Label className="w-40 shrink-0 text-sm">{label}</Label>
    <div className={on ? "flex-1" : "flex-1 opacity-50 pointer-events-none"}>{children}</div>
  </div>
);

const NumSelect = ({ value, onChange, options }: { value: number; onChange: (v: number) => void; options: readonly (readonly [number, string])[] }) => (
  <Select value={String(value)} onValueChange={v => onChange(Number(v))}>
    <SelectTrigger className="h-8 w-48"><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function BulkUpdate() {
  const { logChange } = useDeviceChangeLog();
  const [targetType, setTargetType] = useState<"org" | "group">("org");
  const [targetId, setTargetId] = useState("");
  const [includeSubs, setIncludeSubs] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [apply, setApply] = useState<Record<string, boolean>>({});
  const [v, setV] = useState<Record<string, any>>({
    scheduleStartLocal: 7, scheduleEndLocal: 19, timezone: "Europe/London", intervalMs: 3600000,
    onDemand: true, forceHibernate: false, enable12V: false, cameraSelect: 0,
    brightness: 1, contrast: 1, saturation: 1, ev: 1, iso: 100, absoluteExposure: 0, lightLevel: 50,
    enablePublic: false, enableSponsorship: false, aiEnabled: true, aiModel: AI_MODEL_OPTIONS[0].value,
    workflow: "universal-uk-v1", privacyLevel: 1, query: "", dataTags: "",
  });
  const [confirm, setConfirm] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState<Record<string, { s: Status; msg?: string }>>({});

  const on = (k: string) => !!apply[k];
  const setOn = (k: string) => (b: boolean) => setApply(a => ({ ...a, [k]: b }));
  const set = (k: string) => (val: any) => setV(p => ({ ...p, [k]: val }));

  const { data: orgs = [] } = useQuery({
    queryKey: ["bulk-orgs"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("organizations").select("id, name, parent_organization_id, custom_workflow_id");
      if (error) throw error;
      return sortOrganisationHierarchy(data || []) as any[];
    },
  });
  const { data: groups = [] } = useQuery({
    queryKey: ["bulk-groups"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("device_groups").select("id, name, organization_id").order("name");
      if (error) throw error;
      return data || [];
    },
  });
  const orgName = (id: string | null) => orgs.find(o => o.id === id)?.name ?? "";

  const { data: devices = [], isLoading: loadingDevices } = useQuery({
    queryKey: ["bulk-devices", targetType, targetId, includeSubs],
    enabled: !!targetId,
    queryFn: async () => {
      let q: any = supabase.from("device_details").select("device_id, device_name, organization_id, timezone, notification_ai_model").order("device_name");
      if (targetType === "group") q = q.eq("group_id", targetId);
      else {
        const ids = [targetId, ...(includeSubs ? orgs.filter(o => o.parent_organization_id === targetId).map(o => o.id) : [])];
        q = q.in("organization_id", ids);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as Dev[];
    },
  });

  const selected = devices.filter(d => !excluded.has(d.device_id));
  const appliedCount = Object.values(apply).filter(Boolean).length;

  const changes = useMemo<BulkChanges>(() => {
    const c: any = {};
    for (const [k, b] of Object.entries(apply)) if (b) c[k] = v[k];
    if (c.dataTags !== undefined) c.dataTags = String(c.dataTags).split(",").map((t: string) => t.trim()).filter(Boolean);
    if (c.aiEnabled === false) delete c.aiModel;
    return c;
  }, [apply, v]);

  const runDevice = async (d: Dev) => {
    setStatus(s => ({ ...s, [d.device_id]: { s: "updating" } }));
    try {
      const org = orgs.find(o => o.id === d.organization_id);
      const steps = buildDeviceSteps(changes, { ...d, customWorkflowAvailable: !!org?.custom_workflow_id?.trim() });
      for (const step of steps) {
        if (step.buildship) {
          const [field, value] = step.buildship;
          await updateDeviceField(d.device_id, field, value);
          logChange(d.device_id, field, String(value));
        }
        if (step.db) {
          const { error } = await (supabase as any).from("device_details").update(step.db).eq("device_id", d.device_id);
          if (error) throw new Error(`${step.label}: ${error.message}`);
        }
      }
      setStatus(s => ({ ...s, [d.device_id]: { s: "done" } }));
    } catch (e: any) {
      const skip = e instanceof SkipError;
      setStatus(s => ({ ...s, [d.device_id]: { s: skip ? "skipped" : "failed", msg: e?.message } }));
    }
  };

  const run = async (list: Dev[]) => {
    setRunning(true);
    setStatus(s => { const n = { ...s }; list.forEach(d => (n[d.device_id] = { s: "waiting" })); return n; });
    const queue = [...list];
    await Promise.all(Array.from({ length: BULK_CONCURRENCY }, async () => {
      while (queue.length) {
        const d = queue.shift()!;
        await runDevice(d);
        await new Promise(r => setTimeout(r, 300));
      }
    }));
    setRunning(false);
  };

  const failed = selected.filter(d => status[d.device_id]?.s === "failed");
  const downloadResults = () => {
    const rows = [["Camera", "Device ID", "Organisation", "Result", "Message"], ...selected.map(d => [d.device_name ?? "", d.device_id, orgName(d.organization_id), status[d.device_id]?.s ?? "", status[d.device_id]?.msg ?? ""])];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `bulk-update-${new Date().toISOString().slice(0, 16)}.csv`;
    a.click();
  };

  const badge = (s?: Status) => {
    if (!s) return null;
    const variant = s === "failed" ? "destructive" : s === "done" ? "default" : "secondary";
    return <Badge variant={variant as any} className="capitalize">{s === "updating" && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}{s}</Badge>;
  };

  const levelOpts = LEVELS.map(l => [l, `Level ${l}`] as const);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <FixedPageHeader title="Bulk Update" description="Apply the same camera settings to many cameras at once" icon={Layers} />
          <div className="p-6 space-y-6 max-w-6xl">
            <Card>
              <CardHeader><CardTitle>1. Choose cameras</CardTitle><CardDescription>Pick an organisation or a device group, then untick any cameras to leave out.</CardDescription></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Select value={targetType} onValueChange={t => { setTargetType(t as any); setTargetId(""); setExcluded(new Set()); }}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="org">Organisation</SelectItem><SelectItem value="group">Device group</SelectItem></SelectContent>
                  </Select>
                  <Select value={targetId} onValueChange={id => { setTargetId(id); setExcluded(new Set()); setStatus({}); }}>
                    <SelectTrigger className="w-80"><SelectValue placeholder={targetType === "org" ? "Select organisation" : "Select group"} /></SelectTrigger>
                    <SelectContent>
                      {targetType === "org"
                        ? orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.parent_organization_id ? `↳ ${o.name}` : o.name}</SelectItem>)
                        : groups.map((g: any) => <SelectItem key={g.id} value={g.id}>{g.name}{orgName(g.organization_id) ? ` — ${orgName(g.organization_id)}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {targetType === "org" && (
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={includeSubs} onCheckedChange={b => setIncludeSubs(b === true)} />Include sub-organisations</label>
                  )}
                </div>
                {targetId && (loadingDevices ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <div className="border rounded-md max-h-80 overflow-auto">
                    <div className="flex items-center gap-3 px-3 py-2 border-b bg-muted/50 text-sm font-medium sticky top-0">
                      <Checkbox checked={devices.length > 0 && excluded.size === 0} onCheckedChange={b => setExcluded(b === true ? new Set() : new Set(devices.map(d => d.device_id)))} aria-label="Select all" />
                      {selected.length} of {devices.length} cameras selected
                    </div>
                    {devices.length === 0 && <p className="p-3 text-sm text-muted-foreground">No cameras found.</p>}
                    {devices.map(d => (
                      <div key={d.device_id} className="flex items-center gap-3 px-3 py-1.5 border-b last:border-0 text-sm">
                        <Checkbox checked={!excluded.has(d.device_id)} onCheckedChange={b => setExcluded(s => { const n = new Set(s); b === true ? n.delete(d.device_id) : n.add(d.device_id); return n; })} />
                        <span className="flex-1 truncate">{d.device_name || d.device_id}</span>
                        <span className="text-xs text-muted-foreground w-48 truncate">{orgName(d.organization_id)}</span>
                        <span className="w-24 text-right">{badge(status[d.device_id]?.s)}</span>
                        {status[d.device_id]?.msg && <span className="text-xs text-destructive max-w-56 truncate" title={status[d.device_id]?.msg}>{status[d.device_id]?.msg}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="grid gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader><CardTitle>2a. Configuration</CardTitle><CardDescription>Tick a setting to apply it. Unticked settings stay as they are on each camera.</CardDescription></CardHeader>
                <CardContent>
                  <Row on={on("timezone")} setOn={setOn("timezone")} label="Timezone">
                    <NumSelect value={TIMEZONES.indexOf(v.timezone)} onChange={i => set("timezone")(TIMEZONES[i])} options={TIMEZONES.map((t, i) => [i, t] as const)} />
                  </Row>
                  <Row on={on("scheduleStartLocal")} setOn={setOn("scheduleStartLocal")} label="Schedule start">
                    <NumSelect value={v.scheduleStartLocal} onChange={set("scheduleStartLocal")} options={HOURS.map(h => [h, `${String(h).padStart(2, "0")}:00 local`] as const)} />
                  </Row>
                  <Row on={on("scheduleEndLocal")} setOn={setOn("scheduleEndLocal")} label="Schedule end">
                    <NumSelect value={v.scheduleEndLocal} onChange={set("scheduleEndLocal")} options={HOURS.map(h => [h, `${String(h).padStart(2, "0")}:00 local`] as const)} />
                  </Row>
                  <Row on={on("intervalMs")} setOn={setOn("intervalMs")} label="Capture interval">
                    <NumSelect value={v.intervalMs} onChange={set("intervalMs")} options={INTERVALS} />
                  </Row>
                  {(["onDemand", "forceHibernate", "enable12V", "enablePublic", "enableSponsorship"] as const).map(k => (
                    <Row key={k} on={on(k)} setOn={setOn(k)} label={{ onDemand: "On-demand photo", forceHibernate: "Force hibernate", enable12V: "12V camera power", enablePublic: "Public showcase", enableSponsorship: "Sponsorship" }[k]}>
                      <Switch checked={v[k]} onCheckedChange={set(k)} />
                    </Row>
                  ))}
                  <Row on={on("cameraSelect")} setOn={setOn("cameraSelect")} label="Camera select">
                    <NumSelect value={v.cameraSelect} onChange={set("cameraSelect")} options={[[0, "Camera 0"], [1, "Camera 1"]]} />
                  </Row>
                  {(["brightness", "contrast", "saturation", "ev"] as const).map(k => (
                    <Row key={k} on={on(k)} setOn={setOn(k)} label={k === "ev" ? "EV" : k[0].toUpperCase() + k.slice(1)}>
                      <NumSelect value={v[k]} onChange={set(k)} options={levelOpts} />
                    </Row>
                  ))}
                  <Row on={on("iso")} setOn={setOn("iso")} label="ISO (0–1200)"><Input type="number" min={0} max={1200} className="h-8 w-32" value={v.iso} onChange={e => set("iso")(Number(e.target.value))} /></Row>
                  <Row on={on("absoluteExposure")} setOn={setOn("absoluteExposure")} label="Exposure (0–30000)"><Input type="number" min={0} max={30000} className="h-8 w-32" value={v.absoluteExposure} onChange={e => set("absoluteExposure")(Number(e.target.value))} /></Row>
                  <Row on={on("lightLevel")} setOn={setOn("lightLevel")} label="Light level (1–100)"><Input type="number" min={1} max={100} className="h-8 w-32" value={v.lightLevel} onChange={e => set("lightLevel")(Number(e.target.value))} /></Row>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>2b. AI Setup</CardTitle><CardDescription>Same rules as the camera's AI Setup tab: turning AI on always saves a model first.</CardDescription></CardHeader>
                <CardContent>
                  <Row on={on("aiEnabled")} setOn={setOn("aiEnabled")} label="Enable AI"><Switch checked={v.aiEnabled} onCheckedChange={set("aiEnabled")} /></Row>
                  <Row on={on("aiModel")} setOn={setOn("aiModel")} label="AI model">
                    <Select value={v.aiModel} onValueChange={set("aiModel")}>
                      <SelectTrigger className="h-8 w-56"><SelectValue /></SelectTrigger>
                      <SelectContent>{AI_MODEL_OPTIONS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </Row>
                  <Row on={on("workflow")} setOn={setOn("workflow")} label="Workflow">
                    <Select value={v.workflow} onValueChange={set("workflow")}>
                      <SelectTrigger className="h-8 w-56"><SelectValue /></SelectTrigger>
                      <SelectContent>{WORKFLOWS.map(([val, l]) => <SelectItem key={val} value={val}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </Row>
                  <Row on={on("privacyLevel")} setOn={setOn("privacyLevel")} label="Privacy level"><NumSelect value={v.privacyLevel} onChange={set("privacyLevel")} options={PRIVACY} /></Row>
                  <Row on={on("query")} setOn={setOn("query")} label="AI query"><Textarea rows={4} value={v.query} onChange={e => set("query")(e.target.value)} placeholder="Question the AI asks about each image" /></Row>
                  <Row on={on("dataTags")} setOn={setOn("dataTags")} label="Data tags"><Input className="h-8" value={v.dataTags} onChange={e => set("dataTags")(e.target.value)} placeholder="person, vehicle, cone" /></Row>
                  <p className="text-xs text-muted-foreground mt-3">Areas of Interest are not included — they are drawn per camera view.</p>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button disabled={running || !selected.length || !appliedCount} onClick={() => setConfirm(true)}>
                {running && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Apply {appliedCount} setting{appliedCount === 1 ? "" : "s"} to {selected.length} camera{selected.length === 1 ? "" : "s"}
              </Button>
              {!running && failed.length > 0 && <Button variant="outline" onClick={() => run(failed)}><RotateCcw className="h-4 w-4 mr-2" />Retry {failed.length} failed</Button>}
              {!running && Object.keys(status).length > 0 && <Button variant="outline" onClick={downloadResults}><Download className="h-4 w-4 mr-2" />Download results</Button>}
            </div>
          </div>
        </main>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply {appliedCount} settings to {selected.length} cameras?</AlertDialogTitle>
            <AlertDialogDescription>Each camera is updated in the camera service and database one by one. This cannot be undone automatically.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirm(false); run(selected); }}>Apply</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
}
